/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { Box, Text } from '@radix-ui/themes'
import { useQueries } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { questionService } from '@/services/questionsService'
import { PracTestDetailRes, PracticeTestDetail } from '@/types/practice-test'
import { PracticeTestSection } from '@/types/practice-test'
import {
  CaseStudyQuestion,
  CaseStudyResponse,
  CaseStudyQuestionListResponse,
  QuestionDetail,
} from '@/types/question'
import { clsx } from 'clsx'
import { ArrowRight, Info } from 'lucide-react'
import { useClassStore } from '@/stores/useClassStore'
import { hoverActiveEffect } from '@/utils/style'
import { filterCaseStudyQuestionsByQuestionIds } from '@/utils/practiceTestSectionIds'
import SectionTableView from '@/components/ui/CreateSection/section-table-view'
import TitleBar from '@/components/ui/CreateSection/title-bar'
import { StatBoxProps } from '@/components/ui/SummaryStats/stat-box'
import SummaryStats from '@/components/ui/SummaryStats/summary-stats'
import { Button } from '@/components/ui/button'
import Hstack from '@/components/ui/hstack'
import { Skeleton } from '@/components/ui/skeleton'
import Stack from '@/components/ui/stack'
import MarkIcon from '@/components/svg/MarkIcon'
import Frame1 from '@/components/svg/frame1'
import { ParticipantsIcon } from '@/components/svg/total-student'

interface Props {
  isLoadingPracTestDetail: boolean
  pracTestStat: PracTestDetailRes
  practiceTestDetailData: PracticeTestDetail
  setPage?: (page: number) => void
  setSize?: (size: number) => void
  isLoading?: boolean
}

export default function DetailView({
  isLoadingPracTestDetail,
  pracTestStat,
  practiceTestDetailData,
  setPage,
  setSize: _setSize,
  isLoading,
}: Props) {
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const navigate = useNavigate()

  const { selectedClass } = useClassStore()

  const allCaseStudyIds = useMemo(() => {
    const ids: number[] = []
    const sections = practiceTestDetailData?.sections || []
    sections.forEach((section: PracticeTestSection) => {
      const csData = (
        section?.case_studies as unknown as
          | { data?: CaseStudyResponse | CaseStudyResponse[] }
          | undefined
      )?.data
      const csList = !csData ? [] : Array.isArray(csData) ? csData : [csData]
      csList.forEach((cs) => ids.push(cs.case_study_id))
    })
    return Array.from(new Set(ids))
  }, [practiceTestDetailData])

  const csQueries = useQueries({
    queries: allCaseStudyIds.map((id) => ({
      queryKey: ['case-study-questions', id],
      queryFn: () => questionService.getQuestionByCaseStudy(id),
      enabled: allCaseStudyIds.length > 0,
    })),
  })

  const csUpdatedAtKey = csQueries.map((q) => String(q.dataUpdatedAt)).join('|')

  const csQuestionsMap = useMemo(() => {
    const map: Record<number, CaseStudyQuestion[]> = {}
    allCaseStudyIds.forEach((id, idx) => {
      const resp = csQueries[idx]?.data as unknown as
        | CaseStudyQuestionListResponse
        | undefined
      const list = resp?.questions ?? resp?.question ?? []
      map[id] = list || []
    })
    return map
  }, [allCaseStudyIds.join(','), csUpdatedAtKey])

  const sectionPropsList = useMemo(() => {
    const sections = practiceTestDetailData?.sections || []
    return sections.map((section: PracticeTestSection) => {
      const csData = (
        section?.case_studies as unknown as
          | { data?: CaseStudyResponse | CaseStudyResponse[] }
          | undefined
      )?.data
      const csList = !csData ? [] : Array.isArray(csData) ? csData : [csData]
      const caseStudyGroups = csList.map((cs: any) => {
        const raw = csQuestionsMap[cs.case_study_id] || []
        const items = filterCaseStudyQuestionsByQuestionIds(raw, cs.question_ids)
        let totalTime = 0
        let totalMarks = 0
        for (const q of items) {
          totalTime += q.time || 0
          totalMarks += q.marks || 0
        }
        return {
          caseStudyId: cs.case_study_id,
          items,
          totalTime,
          totalMarks,
          selectedCount: items.length,
        }
      })
      const page = (section as any)?.questions?.meta?.current_page || 1
      const totalPages = (section as any)?.questions?.meta?.total_pages || 1
      const nonCaseQuestions =
        (section?.questions?.data as unknown as QuestionDetail[]).map((q) => ({
          ...q,
          avg_ai_marks: q?.avg_ai_marks,
          avg_tutor_marks: q?.avg_tutor_marks,
        })) || []

      return {
        key: section?.name,
        title: section?.name,
        totals: {
          totalQuestions: section?.total_questions,
          totalMarks: section?.total_marks,
          totalTime: section?.total_time,
        },
        nonCaseQuestions,
        caseStudyGroups,
        page,
        totalPages,
      }
    })
  }, [practiceTestDetailData, csQuestionsMap])

  const totalTime = useMemo(() => {
    if (!practiceTestDetailData) {
      return 0
    }
    // Use duration from API if available, otherwise calculate from sections
    if (
      practiceTestDetailData.duration !== undefined &&
      practiceTestDetailData.duration !== null
    ) {
      return practiceTestDetailData.duration
    }

    const total = practiceTestDetailData.sections?.reduce(
      (acc, item) => (acc += item.total_time),
      0
    )

    return total
  }, [practiceTestDetailData])

  const totalMark = useMemo(() => {
    if (!practiceTestDetailData) {
      return 0
    }
    const total = practiceTestDetailData.sections?.reduce(
      (acc, item) => (acc += item.total_marks),
      0
    )

    return total
  }, [practiceTestDetailData])

  const totalQuestions = useMemo(() => {
    if (!practiceTestDetailData) {
      return 0
    }
    const total = practiceTestDetailData.sections?.reduce(
      (acc, item) => (acc += item.total_questions),
      0
    )

    return total
  }, [practiceTestDetailData])

  const data: StatBoxProps[] = [
    {
      icon: <ParticipantsIcon size={50} />,
      title: 'Participants',
      value: pracTestStat?.participants ?? 'N/A',
      children: (
        <Box className='-mt-1.5'>
          <Button
            className={clsx(
              'w-[102px] border border-[#EAECF0] bg-white text-[#475467]',
              hoverActiveEffect,
              'hover:bg-transparent'
            )}
            onClick={() =>
              navigate({
                to: '/marking/list-participant/$ptId/$classId',
                params: {
                  ptId: practiceTestDetailData.pt_id.toString(),
                  classId: selectedClass?.class_id.toString() ?? 'all',
                },
              })
            }
          >
            View All
            <ArrowRight />
          </Button>
        </Box>
      ),
    },
    {
      icon: <MarkIcon size={48} />,
      title: 'Attempts',
      value: pracTestStat?.attempts ?? 'N/A',
    },
    {
      icon: <MarkIcon size={48} />,
      title: 'Average AI Mark',
      value:
        pracTestStat?.avg_ai_marks !== undefined &&
        pracTestStat?.avg_ai_marks !== null
          ? pracTestStat?.avg_ai_marks
          : 'N/A',

      totalMark: totalMark,
    },
    {
      icon: <MarkIcon size={48} />,
      title: 'Average Tutor Mark',
      value:
        pracTestStat?.avg_tutor_marks !== undefined &&
        pracTestStat?.avg_tutor_marks !== null
          ? pracTestStat?.avg_tutor_marks
          : 'N/A',

      totalMark: totalMark,
    },
  ]

  const data1: StatBoxProps[] = [
    {
      icon: <Frame1 size={50} />,
      title: 'Pass Rate',
      value: pracTestStat?.pass_rate ?? 'N/A',
      valueSuffix:
        pracTestStat?.pass_rate !== null &&
        pracTestStat?.pass_rate !== undefined
          ? '%'
          : '',
      children: (
        <Hstack gap={'73px'} className='ml-[53px]'>
          <Stack justify={'between'} gap={'12px'}>
            <Text className='45% text-[#002652]'>Merit Rate</Text>
            <Text className='text-[60px] !leading-none font-normal text-[#002652]'>
              {pracTestStat?.merit_rate ?? 'N/A'}
              {pracTestStat?.merit_rate !== null &&
                pracTestStat?.merit_rate !== undefined && (
                  <span className='text-[32px]'>%</span>
                )}
            </Text>
          </Stack>
          <Stack justify={'between'} gap={'12px'}>
            <Text className='45% text-[#002652]'>Distinction Rate</Text>
            <Text className='text-[60px] !leading-none font-normal text-[#002652]'>
              {pracTestStat?.distinction_rate ?? 'N/A'}
              {pracTestStat?.distinction_rate !== null &&
                pracTestStat?.distinction_rate !== undefined && (
                  <span className='text-[32px]'>%</span>
                )}
            </Text>
          </Stack>
        </Hstack>
      ),
    },
    {
      icon: <MarkIcon size={48} />,
      title: 'Alignment Rate',
      value: pracTestStat?.alignment_rate ?? 'N/A',
      valueSuffix:
        pracTestStat?.alignment_rate !== null &&
        pracTestStat?.alignment_rate !== undefined
          ? '%'
          : '',
      children: (
        <Hstack gap={'12px'}>
          <Box className='-ml-1 h-fit w-fit cursor-pointer p-1'>
            <Info
              size={15}
              className={clsx(hoverActiveEffect)}
              onMouseOver={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
          </Box>
          <Box
            className={clsx(
              'max-w-[277px] rounded-[15px] bg-[#9AA0A5AD] px-[20px] py-[12px]',
              !showInfo && 'invisible'
            )}
          >
            <Text className='text-[14px] text-[#000000]'>
              The Alignment Rate shows how close the AI's mark is to the tutor's
              mark for each submission.
            </Text>
          </Box>
        </Hstack>
      ),
    },
  ]

  return (
    <Stack gap={'24px'} className='bg-[#F6F7F9] px-[40px]'>
      <TitleBar
        title={practiceTestDetailData?.pt_name}
        status={practiceTestDetailData?.status}
        module={practiceTestDetailData?.module_name}
        totalMarks={totalMark}
        totalQuestions={totalQuestions}
        durationMinutes={totalTime}
        readingTimerEnabled={practiceTestDetailData?.reading_timer_enabled}
        readingDurationSeconds={practiceTestDetailData?.reading_duration_seconds}
      />
      {isLoadingPracTestDetail ? (
        <Stack gap={'16px'} height={'272px'}>
          <Skeleton className='h-full w-full bg-[#d6d6d6]' />
          <Skeleton className='h-full w-full bg-[#d6d6d6]' />
        </Stack>
      ) : (
        <Stack gap={'16px'}>
          <SummaryStats columns={`${data.length}`} gap={'12px'} data={data} />
          <SummaryStats columns={`${data1.length}`} gap={'12px'} data={data1} />
          {practiceTestDetailData?.general_information && (
            <>
              <Text className='text-[24px] font-[400] text-[#002652]'>
                General Information
              </Text>
              {practiceTestDetailData?.general_information && (
                <div
                  className='prose max-w-none'
                  dangerouslySetInnerHTML={{
                    __html:
                      practiceTestDetailData.general_information as string,
                  }}
                />
              )}
            </>
          )}
          {sectionPropsList.map((props) => (
            <SectionTableView
              key={props.key}
              title={props.title}
              totals={props.totals}
              nonCaseQuestions={props.nonCaseQuestions}
              caseStudyGroups={props.caseStudyGroups}
              page={props.page}
              totalPages={props.totalPages}
              onPageChange={setPage as any}
              isLoading={isLoading}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}
